/**
 * Issue #90: pure parse/validate for the Organization's Delivery money
 * settings, kept out of the card component so the rules are testable without
 * rendering. Both amounts are whole numbers in the same units as the rest of
 * Order pricing, and `0` is a legitimate value ("no flat fee" / "no minimum"),
 * which is what every Organization has today.
 */

export type DeliveryPricingPatch = {
  deliveryFeeFlat: number;
  deliveryMinimumOrder: number;
};

export type DeliveryPricingInvalidReason =
  | "fee_not_a_number"
  | "fee_negative"
  | "fee_not_whole"
  | "minimum_not_a_number"
  | "minimum_negative"
  | "minimum_not_whole";

export type DeliveryPricingParseResult =
  | { ok: true; patchBody: DeliveryPricingPatch }
  | { ok: false; reason: DeliveryPricingInvalidReason };

/**
 * An empty input means "zero", so an admin can clear the field to remove the
 * fee or the minimum instead of having to type a literal 0.
 */
function parseAmount(raw: string): number {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return 0;
  }
  return Number(trimmed);
}

export function parseDeliveryPricingInput(input: {
  feeText: string;
  minimumOrderText: string;
}): DeliveryPricingParseResult {
  const fee = parseAmount(input.feeText);
  const minimumOrder = parseAmount(input.minimumOrderText);

  if (!Number.isFinite(fee)) {
    return { ok: false, reason: "fee_not_a_number" };
  }
  if (fee < 0) {
    return { ok: false, reason: "fee_negative" };
  }
  if (!Number.isInteger(fee)) {
    return { ok: false, reason: "fee_not_whole" };
  }
  if (!Number.isFinite(minimumOrder)) {
    return { ok: false, reason: "minimum_not_a_number" };
  }
  if (minimumOrder < 0) {
    return { ok: false, reason: "minimum_negative" };
  }
  if (!Number.isInteger(minimumOrder)) {
    return { ok: false, reason: "minimum_not_whole" };
  }

  return {
    ok: true,
    patchBody: { deliveryFeeFlat: fee, deliveryMinimumOrder: minimumOrder },
  };
}

export type DeliveryPricingSaveDeps = {
  /** The ordering-settings PATCH mutation. */
  update: (patch: DeliveryPricingPatch) => Promise<unknown>;
  onInvalid: (reason: DeliveryPricingInvalidReason) => void;
  onSaved: () => void;
  onError: (error: unknown) => void;
};

/**
 * Validate, then persist. Kept beside the parser (and out of the component) so
 * the "invalid input never reaches the API" guarantee is testable without a DOM.
 */
export async function saveDeliveryPricing(
  input: { feeText: string; minimumOrderText: string },
  deps: DeliveryPricingSaveDeps,
): Promise<void> {
  const parsed = parseDeliveryPricingInput(input);
  if (!parsed.ok) {
    deps.onInvalid(parsed.reason);
    return;
  }
  try {
    await deps.update(parsed.patchBody);
    deps.onSaved();
  } catch (error) {
    deps.onError(error);
  }
}
